'use client'

import { useEffect } from 'react'

type ScriptEntry = {
  id: string
  src?: string
  async?: boolean
  defer?: boolean
  content?: string
}

const parseTrackingCode = (html: string): ScriptEntry[] => {
  const scripts: ScriptEntry[] = []
  const scriptRegex = /<script([^>]*)>([\s\S]*?)<\/script>/gi
  let match: RegExpExecArray | null
  let index = 0

  while ((match = scriptRegex.exec(html)) !== null) {
    const attrs = match[1]
    const content = match[2].trim()
    const src = attrs.match(/src=["']([^"']+)["']/)?.[1]
    const isAsync = /\basync\b/.test(attrs)
    const isDefer = /\bdefer\b/.test(attrs)

    if (src) {
      scripts.push({ id: `tracking-${index++}`, src, async: isAsync, defer: isDefer })
    } else if (content) {
      scripts.push({ id: `tracking-${index++}`, content })
    }
  }

  return scripts
}

type PropsType = {
  code: string | null | undefined
  // Kept for API compatibility; scripts are injected client-side (post-hydration) either way.
  strategy?: 'beforeInteractive' | 'afterInteractive' | 'lazyOnload'
  prefix: string
}

/**
 * Injects tracking scripts (GA/GTM/etc.) into the document head on the client.
 *
 * Rendering executable inline `<script>` tags in the React tree trips React 19's "scripts inside
 * React components are never executed on the client" warning, so instead we create real <script>
 * elements imperatively in an effect — they execute reliably and produce no warning.
 */
const TrackingCodes = ({ code, prefix }: PropsType) => {
  useEffect(() => {
    if (!code?.trim()) {
      return
    }

    const scripts = parseTrackingCode(code)
    const created: HTMLScriptElement[] = []

    scripts.forEach((entry, i) => {
      const id = `${prefix}-${i}`
      if (document.getElementById(id)) {
        return
      }

      const el = document.createElement('script')
      el.id = id

      if (entry.src) {
        el.src = entry.src
        el.async = entry.async ?? true
        if (entry.defer) {
          el.defer = true
        }
      } else if (entry.content) {
        el.textContent = entry.content
      }

      document.head.appendChild(el)
      created.push(el)
    })

    return () => created.forEach(el => el.remove())
  }, [code, prefix])

  return null
}

export default TrackingCodes
